import config from '../config/warmup.json' with { type: 'json' };
import { FLOOR_LIFTS, type MainLift } from './lifts.ts';
import { mass, type Mass } from './mass.ts';
import { floorToLoadable, type Gym } from './plates.ts';

export interface WarmupSet {
  weight: Mass;
  reps: number;
  /** True only for the last warm-up: the rest timer runs after it (ADR-0006/0007). */
  restAfter: boolean;
}

interface UnitParams {
  step: Mass;
  maxJump: Mass;
  snapTolerance: Mass;
  lightRuleAboveBar: Mass;
  floorStart: Partial<Record<MainLift, Mass>>;
  landmarks: Mass[];
}

function params(gym: Gym): UnitParams {
  const c = config[gym.unit];
  return {
    step: mass(c.step),
    maxJump: mass(c.maxJump),
    snapTolerance: mass(c.snapTolerance),
    lightRuleAboveBar: mass(c.lightRuleAboveBar),
    floorStart: Object.fromEntries(Object.entries(c.floorStart).map(([k, v]) => [k, mass(v)])),
    landmarks: c.landmarks.map(mass),
  };
}

const floorTo = (x: number, step: Mass): Mass => Math.floor(x / step) * step;

function nearest(landmarks: Mass[], x: number): Mass {
  return landmarks.reduce((best, l) => (Math.abs(l - x) < Math.abs(best - x) ? l : best));
}

/**
 * House warm-up ladder (ADR-0007). Returns sets below `work`, strictly increasing apart from the
 * two empty-bar sets, each loadable with the gym's plates. Warm-ups never affect progression.
 */
export function warmup(work: Mass, lift: MainLift, gym: Gym): WarmupSet[] {
  const p = params(gym);
  const floorLift = FLOOR_LIFTS.has(lift);
  let start: Mass[];

  if (floorLift) {
    const f = p.floorStart[lift]!;
    if (work <= f) return [];
    start = [floorToLoadable(gym, f) ?? gym.bar];
  } else {
    if (work <= gym.bar) return [];
    start = [gym.bar, gym.bar];
    if (work <= gym.bar + p.lightRuleAboveBar) return finish(start);
  }

  const S = start[start.length - 1]!;
  const T = Math.min(floorTo(work * config.topFraction, p.step), work - 2 * p.step);
  const ladder = [...start];
  if (T > S) {
    const n = Math.max(0, Math.ceil((T - S) / p.maxJump));
    for (let i = 1; i <= n; i++) {
      let x = S + ((T - S) * i) / n;
      const lm = nearest(p.landmarks, x);
      if (i < n) x = Math.abs(lm - x) <= p.snapTolerance && lm < work ? lm : floorTo(x, p.step);
      else x = Math.abs(lm - T) <= p.step && lm < work ? lm : T;
      const loadable = floorToLoadable(gym, x);
      if (loadable !== null && loadable > ladder[ladder.length - 1]! && loadable < work) ladder.push(loadable);
    }
  }
  return finish(ladder);

  function finish(ws: Mass[]): WarmupSet[] {
    return ws.map((weight, i) => ({ weight, reps: config.reps, restAfter: i === ws.length - 1 }));
  }
}
